import { FC } from 'react';
import { Form, Table, Typography } from 'antd';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useQuery } from '@tanstack/react-query';
import { ProspectEmploymentContract } from '@/libs/fleet-api/services/types';
import { getPraSurveyDetail } from '@/libs/fleet-api/services/survey';
import { formatCurrency } from '@/libs/antd/formater';
import { useFBDHVisitReportApprovalPage } from './VisitReportApproval';

type OfficeObservationContract = ProspectEmploymentContract<'payload'> &
	Pick<ProspectEmploymentContract, 'bouwheer'>;

type VisitReportPhotoForm = {
	lists: OfficeObservationContract[];
};

const FBDHVisitReportBouwheer: FC = () => {
	const { mainInformation } = useFBDHVisitReportApprovalPage();
	const { form, fieldName } = useForm<VisitReportPhotoForm>();

	const { data, isLoading } = useQuery({
		queryKey: ['getPraSurveyDetail', mainInformation?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getPraSurveyDetail(id);
		},
		enabled: !!mainInformation?.prospectId,
	});

	if (isLoading) return null;

	const { employmentContracts } = data?.data ?? {};

	return (
		<Form.Provider>
			<Typography.Title level={3}>
				Informasi Bouwheer (Perusahaan/Perorangan)
			</Typography.Title>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists:
							employmentContracts?.map((record) => {
								return {
									...record,
									bouwheerId: record.bouwheer.id,
								};
							}) ?? [],
					} as VisitReportPhotoForm
				}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue }) => {
						const values = getFieldsValue() as VisitReportPhotoForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Table
								bordered
								pagination={false}
								dataSource={dataSource}
								scroll={{
									x: 2000,
								}}
								columns={[
									{
										title: 'No',
										render: (_, __, index) => index + 1,
										width: 1,
									},
									{
										title: 'Bouwheer',
										render: (_, record) => record.bouwheer.name ?? '-',
									},
									{
										title: 'Bouwheer ID',
										render: (_, record) => record.bouwheerId ?? '-',
									},
									{
										title: 'Kelas Industri',
										render: (_, record) =>
											record.bouwheer.industrySector?.industryClass ?? '-',
									},
									{
										title: 'Nama Pekerjaan',
										render: (_, record) => record.jobTitle ?? '-',
									},
									{
										title: 'No. SPK',
										render: (_, record) => record.spkNumber ?? '-',
									},
									{
										title: 'No. SPK',
										render: (_, record) => record.spkNumber ?? '-',
									},
									{
										title: 'Lokasi Pekerjaan',
										render: (_, record) => record.jobLocation ?? '-',
									},
									{
										title: 'Detail Kontrak',
										render: (_, record) => record.contractDetail ?? '-',
									},
									{
										title: 'Nilai Kontrak',
										render: (_, record) =>
											formatCurrency(record.contractValue) ?? '-',
									},
									{
										title: 'Tanggal Mulai',
										render: (_, record) => record.startDate ?? '-',
									},
									{
										title: 'Tanggal Berakhir',
										render: (_, record) => record.endDate ?? '-',
									},
									{
										title: 'Tenor',
										render: () => '-',
									},
									{
										title: 'Sistem Pembayaran',
										render: () => '-',
									},
									{
										title: 'TOP Pembayaran',
										render: () => '-',
									},
									{
										title: 'Verifikasi Bouwheer',
										render: () => '-',
									},
								]}
							/>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FBDHVisitReportBouwheer;
